/**
 * Helpers puros para reaproveitar conversas existentes no formulário de nova conversa.
 * Existe para evitar duplicatas e garantir histórico único: reusa a conversa da
 * mesma caixa (aberta ou resolvida). Fallback: Comercial > Principal > primeira.
 */

const OPEN_STATUSES = ['open', 'pending', 'snoozed'];

// Uma conversa é "aberta" quando ainda não foi resolvida (status != resolved).
export const isConversationOpen = conversation => {
  const status = conversation?.status;
  if (typeof status === 'number') {
    // Enum do Chatwoot: 0 = open, 1 = resolved, 2 = pending, 3 = snoozed.
    return status !== 1;
  }
  return OPEN_STATUSES.includes(status);
};

// Recência determinística: usa o último sinal de atividade disponível.
export const getConversationRecency = conversation => {
  return (
    conversation?.last_activity_at ||
    conversation?.timestamp ||
    conversation?.created_at ||
    conversation?.id ||
    0
  );
};

const byRecencyDesc = (a, b) =>
  getConversationRecency(b) - getConversationRecency(a);

// Conversa ABERTA mais recente do contato numa caixa específica (ou null).
export const findOpenConversationForInbox = (conversations, inboxId) => {
  if (!Array.isArray(conversations) || !inboxId) return null;
  return (
    conversations
      .filter(c => c?.inbox_id === inboxId && isConversationOpen(c))
      .sort(byRecencyDesc)[0] || null
  );
};

// Histórico único: reusa aberta; se não houver, a mais recente (mesmo resolved).
export const findReusableConversationForInbox = (conversations, inboxId) => {
  const open = findOpenConversationForInbox(conversations, inboxId);
  if (open) return open;
  if (!Array.isArray(conversations) || !inboxId) return null;
  return (
    conversations.filter(c => c?.inbox_id === inboxId).sort(byRecencyDesc)[0] ||
    null
  );
};

// Caixa a pré-selecionar: 1) caixa da conversa aberta mais recente (se contatável),
// 2) caixa cujo nome contém "comercial", 3) única caixa contatável, senão null.
export const resolveDefaultInbox = ({
  conversations = [],
  contactInboxes = [],
} = {}) => {
  if (!Array.isArray(contactInboxes) || contactInboxes.length === 0)
    return null;

  const openRecent = [...conversations]
    .filter(isConversationOpen)
    .sort(byRecencyDesc)[0];

  if (openRecent) {
    const match = contactInboxes.find(
      inbox => inbox.id === openRecent.inbox_id
    );
    if (match) return match;
  }

  const nameOf = inbox => (inbox?.name || '').toLowerCase();

  const comercial = contactInboxes.find(inbox =>
    nameOf(inbox).includes('comercial')
  );
  if (comercial) return comercial;

  // Evolution/API: preferir caixa "Principal" quando houver mais de uma.
  const principal = contactInboxes.find(inbox =>
    nameOf(inbox).includes('principal')
  );
  if (principal) return principal;

  if (contactInboxes.length === 1) return contactInboxes[0];

  return contactInboxes[0] || null;
};

// Conversa a abrir ao clicar no contato: aberta mais recente, senão a mais recente.
export const resolveConversationToOpen = conversations => {
  if (!Array.isArray(conversations) || conversations.length === 0) return null;
  const openRecent = conversations
    .filter(isConversationOpen)
    .sort(byRecencyDesc)[0];
  if (openRecent) return openRecent;
  return [...conversations].sort(byRecencyDesc)[0] || null;
};
